import React, { useEffect, useState, useRef } from 'react';
import performanceMonitor from '../utils/performanceMonitor';

interface PerformanceWrapperProps {
  children: React.ReactNode;
  enableMonitoring?: boolean;
  fallbackComponent?: React.ComponentType;
  performanceThreshold?: number;
}

/**
 * Performance-aware wrapper component that monitors FPS and adjusts rendering
 */
const PerformanceWrapper: React.FC<PerformanceWrapperProps> = ({
  children,
  enableMonitoring = true,
  fallbackComponent: FallbackComponent,
  performanceThreshold = 25,
}) => {
  const [isLowPerformance, setIsLowPerformance] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const monitoringRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enableMonitoring) return;

    // Start performance monitoring
    performanceMonitor.startAnimationMonitoring();
    setIsMonitoring(true);

    // Check performance every 5 seconds
    monitoringRef.current = setInterval(() => {
      const avgFPS = performanceMonitor.getAverageFPS();
      const minFPS = performanceMonitor.getMinFPS();
      
      const isPerformancePoor = avgFPS < performanceThreshold || minFPS < 15;
      
      if (isPerformancePoor !== isLowPerformance) {
        setIsLowPerformance(isPerformancePoor);
        
        // Add/remove performance mode class
        if (isPerformancePoor) {
          document.documentElement.classList.add('performance-mode');
          console.warn('Performance mode enabled - reducing animation complexity');
        } else {
          document.documentElement.classList.remove('performance-mode');
          console.log('Performance mode disabled - normal animations restored');
        }
      }
    }, 5000);

    // Listen for performance events
    const handlePerformanceModeEnabled = (event: CustomEvent) => {
      setIsLowPerformance(true);
      console.warn('Performance mode enabled:', event.detail);
    };

    const handlePerformanceModeDisabled = (event: CustomEvent) => {
      setIsLowPerformance(false);
      console.log('Performance mode disabled:', event.detail);
    };

    window.addEventListener('performance-mode-enabled', handlePerformanceModeEnabled as EventListener);
    window.addEventListener('performance-mode-disabled', handlePerformanceModeDisabled as EventListener);

    return () => {
      if (monitoringRef.current) {
        clearInterval(monitoringRef.current);
      }
      
      performanceMonitor.stopAnimationMonitoring();
      setIsMonitoring(false);
      
      window.removeEventListener('performance-mode-enabled', handlePerformanceModeEnabled as EventListener);
      window.removeEventListener('performance-mode-disabled', handlePerformanceModeDisabled as EventListener);
    };
  }, [enableMonitoring, performanceThreshold, isLowPerformance]);

  // Render fallback component if performance is poor and fallback is provided
  if (isLowPerformance && FallbackComponent) {
    return <FallbackComponent />;
  }

  return (
    <div 
      className={`performance-wrapper ${isLowPerformance ? 'low-performance' : ''}`}
      data-performance-monitoring={isMonitoring}
      data-low-performance={isLowPerformance}
    >
      {children}
    </div>
  );
};

export default PerformanceWrapper;